import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  useMediaQuery,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  FormControl,
} from '@mui/material';
import axios from 'axios';
import { DonutComponent } from './DonutComponent';
import ResearchProjectTableComponent from './ResearchProjectTableComponent';
import { DASHBOARD_SERVER } from '../../Config';
import { getDomainColor, getDataTargetColor } from './chartColors';

axios.defaults.withCredentials = true;

// 코드 정의 목록(allXxxNames)에 '미입력' 옵션을 추가해 필터 드롭다운에서
// 값이 비어있는 데이터도 선택할 수 있도록 정규화한다.
// (allDomainNames/allDataTargetNames/allAccessLevelNames는 Code 컬렉션의 실제 코드 정의 목록이라
//  '미입력'이라는 코드값 자체가 존재하지 않으므로, 프론트에서 항상 마지막에 추가해줘야 한다.)
const withEmptyOption = (names, excludeKeyword) => {
  if (!Array.isArray(names)) return [];
  const cleaned = names.filter((name) => name && !name.includes(excludeKeyword));
  const unique = [...new Set(cleaned)];
  if (!unique.includes('미입력')) {
    unique.push('미입력');
  }
  return unique;
};

const DashboardPage = () => {
  const [domain, setDomain] = useState([]);
  const [domainTotal, setDomainTotal] = useState(0);
  const [dataTarget, setDataTarget] = useState([]);
  const [dataTargetTotal, setDataTargetTotal] = useState(0);

  // 전체 과제 수 (Organization.code 기준, 문서 전체 개수)
  const [projectTotalCount, setProjectTotalCount] = useState(0);
  // 고유 기관 수 (Organization.name 기준 중복 제거)
  const [organizationCount, setOrganizationCount] = useState(0);

  // 검색 및 필터 상태
  const [searchText, setSearchText] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [dataTargetFilter, setDataTargetFilter] = useState('');
  const [accessLevelFilter, setAccessLevelFilter] = useState('');

  // 과제별 통계 데이터
  const [projectStats, setProjectStats] = useState({
    totalProjects: 0,
    projects: [],
  });

  // 전체 코드명 목록 / 도메인 구조 (DB 조회 결과)
  const [allDomainNames, setAllDomainNames] = useState([]);
  const [allDataTargetNames, setAllDataTargetNames] = useState([]);
  const [allAccessLevelNames, setAllAccessLevelNames] = useState([]);
  const [domainStructure, setDomainStructure] = useState([]);

  const matches = useMediaQuery('(max-width:768px)');

  // 전체 통계 조회 - 최초 로드 시
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_SERVER}${DASHBOARD_SERVER}`)
      .then((res) => {
        const data = res.data || {};
        setDomain(Array.isArray(data.domain) ? data.domain : []);
        setDomainTotal(Number(data.domainTotal || 0));
        setDataTarget(Array.isArray(data.dataTarget) ? data.dataTarget : []);
        setDataTargetTotal(Number(data.dataTargetTotal || 0));
        setProjectTotalCount(Number(data.projectTotalCount || 0));
        setOrganizationCount(Number(data.organizationTotalCount || 0));
      })
      .catch((err) => {
        console.error('dashboard fetch error:', err);
        setDomain([]);
        setDomainTotal(0);
        setDataTarget([]);
        setDataTargetTotal(0);
        setProjectTotalCount(0);
        setOrganizationCount(0);
      });
  }, []);

  // 과제별 상세 통계 조회 - 검색 및 필터 변경 시
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchText) params.append('search', searchText);
    if (domainFilter) params.append('domainFilter', domainFilter);
    if (dataTargetFilter) params.append('dataTargetFilter', dataTargetFilter);
    if (accessLevelFilter) params.append('accessLevelFilter', accessLevelFilter);

    axios
      .get(`${process.env.REACT_APP_SERVER}${DASHBOARD_SERVER}/project-stats?${params.toString()}`)
      .then((res) => {
        const data = res.data || {};
        setProjectStats({
          totalProjects: Number(data.totalProjects || 0),
          projects: Array.isArray(data.projects) ? data.projects : [],
        });

        // 전체 코드명 / 도메인 구조 저장
        if (data.domainNames) setAllDomainNames(data.domainNames);
        if (data.dataTargetNames) setAllDataTargetNames(data.dataTargetNames);
        if (data.accessLevelNames) setAllAccessLevelNames(data.accessLevelNames);
        if (data.domainStructure) setDomainStructure(data.domainStructure);
      })
      .catch((err) => {
        console.error('Project Stats API Error:', err);
        setProjectStats({ totalProjects: 0, projects: [] });
      });
  }, [searchText, domainFilter, dataTargetFilter, accessLevelFilter]);

  // 도메인 필터 옵션 - '미입력' 포함
  const domainFilterOptions = useMemo(() => withEmptyOption(allDomainNames, 'Domain'), [allDomainNames]);

  // 데이터 대상 필터 옵션 - '미입력' 포함
  const dataTargetFilterOptions = useMemo(
    () => withEmptyOption(allDataTargetNames, 'Data Target'),
    [allDataTargetNames]
  );

  // 접근 권한 필터 옵션 - '미입력' 포함
  const accessLevelFilterOptions = useMemo(
    () => withEmptyOption(allAccessLevelNames, 'Access Level'),
    [allAccessLevelNames]
  );

  // 테이블 제목 및 서브헤더
  const tableTitle = '연구 과제별 데이터 현황';
  const tableSubheader = `Total : ${projectStats.totalProjects}개 연구 과제`;

  const domainChartColors = useMemo(() => {
    return domain.map((item) => getDomainColor(item.label));
  }, [domain]);

  const dataTargetChartColors = useMemo(() => {
    return dataTarget.map((item) => getDataTargetColor(item.label));
  }, [dataTarget]);

  return (
    <div className="DashboardPage">
      <Container
        maxWidth="xl"
        sx={matches ? { paddingTop: '116px', paddingBottom: '100px' } : { paddingTop: '200px', paddingBottom: '100px' }}
      >
        {/* 상단 요약 카드 - 전체 과제 수 / 참여 기관 수 */}
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }} sx={{ mb: 4 }}>
            <Card elevation={3}>
              <CardHeader title="전체 과제 수" />
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {projectTotalCount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    전체 과제 수
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={3}>
              <CardHeader title="참여 기관 수" />
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {organizationCount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    참여 기관 수
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 도메인 / 데이터 대상 도넛 차트 */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {domain.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <DonutComponent
                title="도메인별 데이터 현황"
                subheader={`Total : ${domainTotal}`}
                chartData={domain}
                chartColors={domainChartColors}
              />
            </Grid>
          )}
          {dataTarget.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <DonutComponent
                title="데이터 대상별 데이터 현황"
                subheader={`Total : ${dataTargetTotal}`}
                chartData={dataTarget}
                chartColors={dataTargetChartColors}
              />
            </Grid>
          )}
        </Grid>

        {/* 연구 과제별 데이터 현황 - 검색/필터 및 테이블 통합 */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {tableTitle}
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="과제명 검색"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="과제명을 입력하세요"
              sx={{ flex: '1 1 200px' }}
            />

            <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
              <InputLabel id="domain-filter-label">도메인</InputLabel>
              <Select
                labelId="domain-filter-label"
                value={domainFilter}
                label="도메인"
                onChange={(e) => setDomainFilter(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                <MenuItem value="">전체</MenuItem>
                {domainFilterOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
              <InputLabel id="data-target-filter-label">데이터 대상</InputLabel>
              <Select
                labelId="data-target-filter-label"
                value={dataTargetFilter}
                label="데이터 대상"
                onChange={(e) => setDataTargetFilter(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                <MenuItem value="">전체</MenuItem>
                {dataTargetFilterOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
              <InputLabel id="access-level-filter-label">접근 권한</InputLabel>
              <Select
                labelId="access-level-filter-label"
                value={accessLevelFilter}
                label="접근 권한"
                onChange={(e) => setAccessLevelFilter(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                <MenuItem value="">전체</MenuItem>
                {accessLevelFilterOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchText('');
                setDomainFilter('');
                setDataTargetFilter('');
                setAccessLevelFilter('');
              }}
              sx={{ height: '56px', flexShrink: 0 }}
            >
              초기화
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {tableSubheader}
          </Typography>

          <Box
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 4 },
            }}
          >
            <ResearchProjectTableComponent
              projects={projectStats.projects}
              allDomainNames={allDomainNames}
              allDataTargetNames={allDataTargetNames}
              allAccessLevelNames={allAccessLevelNames}
              domainStructure={domainStructure}
            />
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default DashboardPage;
